'use client';

/**
 * Credit usage overlay — opened from the workspace dropdown behind the
 * `usage-based-billing-on-dashboard` flag. Renders three plan variants
 * (credit packs, unlimited, pay as you go) from one shared shell.
 *
 * Built on `@mantine/core` per WEB-ADR-001 (ui-lib is deprecated), consistent
 * with `CreditUsageMenuItem`. Mantine components used here: `Modal` (title +
 * close button, geometry from the shared theme), `SimpleGrid` (two columns that
 * stack at phone width), `Stack`, `Group`, `Title`, `Text`, `Progress`,
 * `Skeleton`, `Button`. Content padding and the footer rule come from the shared
 * `@mantine-components/ModalContent` and `@mantine-components/ModalFooter`
 * wrappers, matching the other Mantine modals in the app.
 */

import {
  Button,
  Group,
  Modal,
  Progress,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { ModalContent } from '@mantine-components/ModalContent';
import { ModalFooter } from '@mantine-components/ModalFooter';
import { tokens } from 'ui-lib/src/lib/css/tokens';

import { CreditUsageBreakdownTable } from './CreditUsageBreakdownTable';
import { CreditUsagePlanCard } from './CreditUsagePlanCard';
import type { CreditPlanKind, CreditSummary } from './derive-credit-summary-view';
import { formatCreditCount, formatPeriodEndingDate } from './format';
import { meterColor } from './styles.css';
import { useCreditSummary } from './use-credit-summary';

/** Matches the 847px design frame; Mantine caps it to the viewport when narrower. */
const OVERLAY_WIDTH = '847px';

interface CreditUsageOverlayProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  onAddCredits: () => void;
}

interface UsageHeroProps {
  planKind: CreditPlanKind;
  summary: CreditSummary;
  purchasedMilli: number;
  consumedFromPacksMilli: number;
}

function UsageHero({ planKind, summary, purchasedMilli, consumedFromPacksMilli }: UsageHeroProps) {
  // Pay as you go has no credit balance to meter, so it names the period instead.
  if (planKind === 'PAYG') {
    return (
      <Group gap={4} wrap="nowrap">
        <Text c="dimmed" size="sm" lh="20px">
          Period ending
        </Text>
        <Text fw={600} size="sm" lh="20px">
          {formatPeriodEndingDate(summary.periodEnd)}
        </Text>
      </Group>
    );
  }

  // Both metered plans draw the bar against the purchased pack credits, reading
  // the milli values directly.
  // Credit packs lead with used / purchased; unlimited has no ceiling on lead
  // capture, so it leads with the month's usage alone.
  const percentageConsumed =
    purchasedMilli > 0 ? Math.min(100, (consumedFromPacksMilli / purchasedMilli) * 100) : 0;
  const headline =
    planKind === 'CREDIT_PACKS'
      ? `${formatCreditCount(consumedFromPacksMilli)} / ${formatCreditCount(purchasedMilli)}`
      : formatCreditCount(summary.consumedThisMonthMilli);

  return (
    // The design leaves 10px under the bar before the column gap (Figma `2413:11482`).
    <Stack gap="sm" pb={10}>
      <Group gap="xs" align="baseline" wrap="nowrap">
        <Title order={3}>{headline}</Title>
        <Text size="sm" lh="20px" c={tokens.color.foreground25.contentTertiary}>
          credits used
        </Text>
      </Group>
      <Progress value={percentageConsumed} size={5} radius={3} color={meterColor} />
    </Stack>
  );
}

/** One placeholder line per breakdown row: header, four categories, total. */
const BREAKDOWN_SKELETON_ROWS = ['header', 'capture', 'enrichment', 'bulk', 'other', 'total'];

/**
 * Mirrors the loaded overlay's two columns while the summary is in flight, so the
 * modal keeps its shape instead of swapping a spinner for a full layout. Never
 * renders zeros, which would read as real figures.
 */
function CreditUsageOverlaySkeleton() {
  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="lg" aria-hidden>
      <Stack gap="md">
        <Stack gap="sm">
          <Skeleton height={30} width="55%" />
          <Skeleton height={5} />
        </Stack>
        <Skeleton height={200} radius="md" />
      </Stack>
      <Stack gap="sm">
        <Skeleton height={20} width="70%" />
        {BREAKDOWN_SKELETON_ROWS.map((row) => (
          <Skeleton key={row} height={24} />
        ))}
      </Stack>
    </SimpleGrid>
  );
}

/**
 * Shown when the summary query fails, so a failure reads as a failure rather
 * than a skeleton that never resolves. Retrying refetches in place.
 */
function CreditUsageOverlayError({ onRetry }: { onRetry: () => void }) {
  return (
    <Stack gap="xs" align="center" py="xl">
      <Text fw={600}>Credit usage is unavailable</Text>
      <Text size="sm" c="dimmed" ta="center">
        We could not load this workspace's credit usage just now.
      </Text>
      <Button variant="outline" size="sm" mt="xs" onClick={onRetry}>
        Try again
      </Button>
    </Stack>
  );
}

export function CreditUsageOverlay({
  workspaceId,
  open,
  onClose,
  onAddCredits,
}: CreditUsageOverlayProps) {
  // The overlay stays mounted alongside the workspace dropdown, so the summary —
  // a full-month derivation in the ledger — is only fetched once it is opened.
  const {
    summary,
    failed,
    retry,
    planKind,
    purchasedMilli,
    consumedFromPacksMilli,
    availableMilli,
    renewalDate,
  } = useCreditSummary(workspaceId, { skip: !open });

  const body = (() => {
    if (failed) {
      return <CreditUsageOverlayError onRetry={retry} />;
    }

    if (summary === null) {
      return <CreditUsageOverlaySkeleton />;
    }

    return (
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="lg">
        <Stack gap="md">
          <UsageHero
            planKind={planKind}
            summary={summary}
            purchasedMilli={purchasedMilli}
            consumedFromPacksMilli={consumedFromPacksMilli}
          />
          <CreditUsagePlanCard
            planKind={planKind}
            summary={summary}
            purchasedMilli={purchasedMilli}
            consumedFromPacksMilli={consumedFromPacksMilli}
            availableMilli={availableMilli}
            renewalDate={renewalDate}
          />
        </Stack>
        <CreditUsageBreakdownTable planKind={planKind} summary={summary} />
      </SimpleGrid>
    );
  })();

  return (
    <Modal
      opened={open}
      onClose={onClose}
      size={OVERLAY_WIDTH}
      withCloseButton
      title={
        <Text fw={600} size="lg" lh="28px">
          Credit usage
        </Text>
      }
    >
      <ModalContent p="lg">{body}</ModalContent>
      <ModalFooter>
        <Group justify="flex-end" gap="sm">
          <Button variant="outline" size="md" onClick={onClose}>
            Close
          </Button>
          {/*
            The CTA label is derived from the plan, so it waits for the summary —
            as a placeholder of the button's own size, to keep the footer still.
            A failed load offers no CTA at all: there is no plan to act on.
          */}
          {summary === null && !failed && (
            <Skeleton height={40} width={124} radius="md" aria-hidden />
          )}
          {summary !== null && (
            <Button size="md" onClick={onAddCredits}>
              {planKind === 'PAYG' ? 'Add credit plan' : 'Add credits'}
            </Button>
          )}
        </Group>
      </ModalFooter>
    </Modal>
  );
}
